import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { invariant, ErrorConstant, ExtensionTag, object, values, includes, extension, ExtensionPriority, command, helper, NodeExtension, convertCommand, nonChainable, findParentNodeOfType } from '@remirror/core';
import { TextSelection } from '@remirror/pm/state';
import { fixTablesKey, columnResizing, tableEditing, deleteTable, addColumnBefore, addColumnAfter, deleteColumn, addRowBefore, addRowAfter, deleteRow, mergeCells, splitCell, toggleHeaderColumn, toggleHeaderRow, toggleHeaderCell, setCellAttr, fixTables, CellSelection } from '@remirror/pm/tables';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import { ExtensionTablesMessages } from '@remirror/messages';

// Helper for creating a schema that supports tables.
function getCellAttrs(dom) {
  var _dom$getAttribute, _dom$getAttribute2;

  var widthAttr = dom.getAttribute('data-colwidth');
  var widths = widthAttr && /^\d+(,\d+)*$/.test(widthAttr) ? widthAttr.split(',').map(s => Number(s)) : null;
  var colspan = Number((_dom$getAttribute = dom.getAttribute('colspan')) !== null && _dom$getAttribute !== void 0 ? _dom$getAttribute : 1);
  return {
    colspan,
    rowspan: Number((_dom$getAttribute2 = dom.getAttribute('rowspan')) !== null && _dom$getAttribute2 !== void 0 ? _dom$getAttribute2 : 1),
    colwidth: widths && widths.length === colspan ? widths : null,
    background: dom.style.backgroundColor || null
  };
}

function setCellAttrs(node) {
  var attrs = {};

  if (node.attrs.colspan !== 1) {
    attrs.colspan = node.attrs.colspan;
  }

  if (node.attrs.rowspan !== 1) {
    attrs.rowspan = node.attrs.rowspan;
  }

  if (node.attrs.colwidth) {
    attrs['data-colwidth'] = node.attrs.colwidth.join(',');
  }

  if (node.attrs.background) {
    var _attrs$style;

    attrs.style = "".concat((_attrs$style = attrs.style) !== null && _attrs$style !== void 0 ? _attrs$style : '', "background-color: ").concat(node.attrs.background, ";");
  }

  return attrs;
}
/**
 * This function creates the base for the tableNode ProseMirror specs.
 */


function createTableNodeSchema(extra, override) {
  var _override$parseDOM, _override$parseDOM2, _override$parseDOM3, _override$parseDOM4;

  var cellAttrs = _objectSpread(_objectSpread({}, extra.defaults()), {}, {
    colspan: {
      default: 1
    },
    rowspan: {
      default: 1
    },
    colwidth: {
      default: null
    },
    background: {
      default: null
    }
  });

  return {
    table: _objectSpread(_objectSpread({
      isolating: true
    }, override), {}, {
      attrs: extra.defaults(),
      content: 'tableRow+',
      tableRole: 'table',
      parseDOM: [{
        tag: 'table',
        getAttrs: extra.parse
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],

      toDOM(node) {
        return ['table', ['tbody', extra.dom(node), 0]];
      }

    }),
    tableRow: _objectSpread(_objectSpread({}, override), {}, {
      attrs: extra.defaults(),
      content: '(tableCell | tableHeaderCell)*',
      tableRole: 'row',
      parseDOM: [{
        tag: 'tr',
        getAttrs: extra.parse
      }, ...((_override$parseDOM2 = override.parseDOM) !== null && _override$parseDOM2 !== void 0 ? _override$parseDOM2 : [])],

      toDOM(node) {
        return ['tr', extra.dom(node), 0];
      }

    }),
    tableCell: _objectSpread(_objectSpread({
      isolating: true,
      content: "".concat(ExtensionTag.Block, "+")
    }, override), {}, {
      attrs: cellAttrs,
      tableRole: 'cell',
      parseDOM: [{
        tag: 'td',
        getAttrs: dom => _objectSpread(_objectSpread({}, extra.parse(dom)), getCellAttrs(dom))
      }, ...((_override$parseDOM3 = override.parseDOM) !== null && _override$parseDOM3 !== void 0 ? _override$parseDOM3 : [])],

      toDOM(node) {
        return ['td', _objectSpread(_objectSpread({}, extra.dom(node)), setCellAttrs(node)), 0];
      }

    }),
    tableHeaderCell: _objectSpread(_objectSpread({
      isolating: true,
      content: "".concat(ExtensionTag.Block, "+")
    }, override), {}, {
      attrs: cellAttrs,
      tableRole: 'header_cell',
      parseDOM: [{
        tag: 'th',
        getAttrs: dom => _objectSpread(_objectSpread({}, extra.parse(dom)), getCellAttrs(dom))
      }, ...((_override$parseDOM4 = override.parseDOM) !== null && _override$parseDOM4 !== void 0 ? _override$parseDOM4 : [])],

      toDOM(node) {
        return ['th', _objectSpread(_objectSpread({}, extra.dom(node)), setCellAttrs(node)), 0];
      }

    })
  };
}
var TABLE_ROLES = ['table', 'row', 'cell', 'header_cell'];

/**
 * Returns a map where keys are tableRoles and values are NodeTypes.
 */
function tableNodeTypes(schema) {
  if (schema.cached.tableNodeTypes) {
    return schema.cached.tableNodeTypes;
  }

  var roles = object();
  schema.cached.tableNodeTypes = roles;

  var _iterator = _createForOfIteratorHelper(values(schema.nodes)),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var nodeType = _step.value;

      if (includes(TABLE_ROLES, nodeType.spec.tableRole)) {
        roles[nodeType.spec.tableRole] = nodeType;
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return roles;
}

/**
 * Create a cell with the provided content.
 */
function createCell(props) {
  var content = props.content,
      type = props.type;

  if (content) {
    return type.createChecked(null, content);
  }

  return type.createAndFill();
}
/**
 * Returns a table node of a given size.
 *
 * @remarks
 *
 * ```ts
 * const table = createTable({ schema: state.schema }); // 3x3 table node
 * dispatch(tr.replaceSelectionWith(table).scrollIntoView());
 * ```
 */


function createTable(props) {
  var schema = props.schema,
      cellContent = props.cellContent,
      _props$columnsCount = props.columnsCount,
      columnsCount = _props$columnsCount === void 0 ? 3 : _props$columnsCount,
      _props$rowsCount = props.rowsCount,
      rowsCount = _props$rowsCount === void 0 ? 3 : _props$rowsCount,
      _props$withHeaderRow = props.withHeaderRow,
      withHeaderRow = _props$withHeaderRow === void 0 ? true : _props$withHeaderRow;

  var _tableNodeTypes = tableNodeTypes(schema),
      tableCell = _tableNodeTypes.cell,
      tableHeaderCell = _tableNodeTypes.header_cell,
      tableRow = _tableNodeTypes.row,
      table = _tableNodeTypes.table;

  !(tableCell && tableHeaderCell && tableRow && table) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.EXTENSION,
    message: 'Table Extension unable to locate required nodes.'
  }) : invariant(false) : void 0;
  var cells = [];
  var headerCells = [];

  for (var ii = 0; ii < columnsCount; ii++) {
    cells.push(createCell({
      type: tableCell,
      content: cellContent
    }));

    if (withHeaderRow) {
      headerCells.push(createCell({
        type: tableHeaderCell,
        content: cellContent
      }));
    }
  }

  var rows = [];

  for (var _ii = 0; _ii < rowsCount; _ii++) {
    var rowNodes = withHeaderRow && _ii === 0 ? headerCells : cells;
    rows.push(tableRow.createChecked(null, rowNodes));
  }

  return table.createChecked(null, rows);
}
var CREATE_COMMAND_DESCRIPTION = ExtensionTablesMessages.CREATE_COMMAND_DESCRIPTION,
    CREATE_COMMAND_LABEL = ExtensionTablesMessages.CREATE_COMMAND_LABEL;
var createTableOptions = {
  icon: 'table2',
  description: _ref => {
    var t = _ref.t;
    return t(CREATE_COMMAND_DESCRIPTION);
  },
  label: _ref2 => {
    var t = _ref2.t;
    return t(CREATE_COMMAND_LABEL);
  }
};

var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _dec10, _dec11, _dec12, _dec13, _dec14, _dec15, _dec16, _dec17, _dec18, _dec19, _class, _class2, _dec20, _class4, _dec21, _class5, _dec22, _class6;
var tablesEnabled = false;
var TableExtension = (_dec = extension({
  defaultOptions: {
    resizable: true
  },
  defaultPriority: ExtensionPriority.Default
}), _dec2 = command(createTableOptions), _dec3 = command(), _dec4 = command(), _dec5 = command(), _dec6 = command(), _dec7 = command(), _dec8 = command(), _dec9 = command(), _dec10 = command(), _dec11 = command(), _dec12 = command(), _dec13 = command(), _dec14 = command(), _dec15 = command(), _dec16 = command(), _dec17 = command({
  disableChaining: true
}), _dec18 = helper(), _dec19 = command(), _dec(_class = (_class2 = class TableExtension extends NodeExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "lastGoodState", undefined);
  }

  get name() {
    return 'table';
  }

  createTags() {
    return [ExtensionTag.Block];
  }
  /**
   * The last known good state that didn't need fixing. This helps make the fix
   * command more effective.
   */


  createNodeSpec(extra, override) {
    return createTableNodeSchema(extra, override).table;
  }
  /**
   * Create the table extensions. Set the priority to low so that they appear
   * lower down in the node list.
   */


  createExtensions() {
    return [new TableRowExtension({
      priority: ExtensionPriority.Low
    })];
  }

  onStateUpdate(props) {
    var _tr$getMeta;

    var tr = props.tr,
        state = props.state;

    if (tr !== null && tr !== void 0 && (_tr$getMeta = tr.getMeta(fixTablesKey)) !== null && _tr$getMeta !== void 0 && _tr$getMeta.fixTables) {
      this.lastGoodState = state;
    }
  }
  /**
   * Add the table plugins to the editor.
   */


  createExternalPlugins() {
    var plugins = [];

    if (this.options.resizable) {
      // Add first to avoid highlighting cells while resizing
      plugins.push(columnResizing({}));
    }

    plugins.push(tableEditing());
    return plugins;
  }
  /**
   * Create a table in the editor at the current selection point.
   */


  createTable() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return props => {
      var tr = props.tr,
          dispatch = props.dispatch,
          state = props.state;

      if (!tr.selection.empty) {
        return false;
      }

      var offset = tr.selection.anchor + 1;
      var nodes = createTable(_objectSpread({
        schema: state.schema
      }, options));
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.replaceSelectionWith(nodes).scrollIntoView().setSelection(TextSelection.near(tr.doc.resolve(offset))));
      return true;
    };
  }
  /**
   * Delete the table.
   */


  deleteTable() {
    return convertCommand(deleteTable);
  }
  /**
   * Command to add a column before the column with the selection.
   */


  addTableColumnBefore() {
    return convertCommand(addColumnBefore);
  }
  /**
   * Command to add a column after the column with the selection.
   */


  addTableColumnAfter() {
    return convertCommand(addColumnAfter);
  }
  /**
   * Remove selected column from the table.
   */


  deleteTableColumn() {
    return convertCommand(deleteColumn);
  }
  /**
   * Add a table row before the current selection.
   */


  addTableRowBefore() {
    return convertCommand(addRowBefore);
  }
  /**
   * Add a table row after the current selection.
   */


  addTableRowAfter() {
    return convertCommand(addRowAfter);
  }
  /**
   * Delete the table row at the current selection.
   */


  deleteTableRow() {
    return convertCommand(deleteRow);
  }
  /**
   * Toggles between merging cells.
   */


  toggleTableCellMerge() {
    return toggleMergeCellCommand;
  }
  /**
   * Merge the table cells.
   */


  mergeTableCells() {
    return convertCommand(mergeCells);
  }
  /**
   * Split the merged cells into individual cells.
   */


  splitTableCell() {
    return convertCommand(splitCell);
  }
  /**
   * Toggles a column as the header column.
   */


  toggleTableHeaderColumn() {
    return convertCommand(toggleHeaderColumn);
  }
  /**
   * Toggles a row as a table header row.
   */


  toggleTableHeaderRow() {
    return convertCommand(toggleHeaderRow);
  }
  /**
   * Toggle a cell as a table header cell.
   */


  toggleTableHeaderCell() {
    return convertCommand(toggleHeaderCell);
  }
  /**
   * Set the attribute for a table cell.
   */


  setTableCellAttribute(name, value) {
    return convertCommand(setCellAttr(name, value));
  }
  /**
   * Fix all tables within the document.
   *
   * This is a **non-chainable** command.
   */


  fixTables() {
    return nonChainable(fixTablesCommand(this.lastGoodState));
  }
  /**
   * Enable table usage within the editor. This depends on the browser that
   * is being used.
   */


  enableTableSupport() {
    if (!tablesEnabled) {
      document.execCommand('enableObjectResizing', false, 'false');
      document.execCommand('enableInlineTableEditing', false, 'false');
      tablesEnabled = true;
    }
  }
  /**
   * Update the background of one cell or multiple cells by passing a color
   * string. You can also remove the color by passing a `null`.
   */


  setTableCellBackground(background) {
    return props => {
      var tr = props.tr;
      var dispatch = props.dispatch;
      var _tr = tr,
          selection = _tr.selection;

      if (selection instanceof CellSelection) {
        selection.forEachCell((cellNode, pos) => {
          tr = tr.setNodeMarkup(pos, undefined, _objectSpread(_objectSpread({}, cellNode.attrs), {}, {
            background
          }));
        });
        dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr);
        return true;
      }

      var found = findParentNodeOfType({
        selection,
        types: 'tableCell'
      });

      if (found) {
        dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(found.pos, undefined, _objectSpread(_objectSpread({}, found.node.attrs), {}, {
          background
        })));
        return true;
      }

      return false;
    };
  }
  /**
   * This managers the updates of the collaboration provider.
   */


  onSetOptions(props) {
    var changes = props.changes; // TODO move this into a new method in `plugins-extension`.

    if (changes.resizable.changed) {
      this.store.updateExtensionPlugins(this);
    }
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "createTable", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "createTable"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "deleteTable", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "deleteTable"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "addTableColumnBefore", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "addTableColumnBefore"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "addTableColumnAfter", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "addTableColumnAfter"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "deleteTableColumn", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "deleteTableColumn"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "addTableRowBefore", [_dec7], Object.getOwnPropertyDescriptor(_class2.prototype, "addTableRowBefore"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "addTableRowAfter", [_dec8], Object.getOwnPropertyDescriptor(_class2.prototype, "addTableRowAfter"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "deleteTableRow", [_dec9], Object.getOwnPropertyDescriptor(_class2.prototype, "deleteTableRow"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleTableCellMerge", [_dec10], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleTableCellMerge"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "mergeTableCells", [_dec11], Object.getOwnPropertyDescriptor(_class2.prototype, "mergeTableCells"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "splitTableCell", [_dec12], Object.getOwnPropertyDescriptor(_class2.prototype, "splitTableCell"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleTableHeaderColumn", [_dec13], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleTableHeaderColumn"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleTableHeaderRow", [_dec14], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleTableHeaderRow"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleTableHeaderCell", [_dec15], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleTableHeaderCell"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setTableCellAttribute", [_dec16], Object.getOwnPropertyDescriptor(_class2.prototype, "setTableCellAttribute"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "fixTables", [_dec17], Object.getOwnPropertyDescriptor(_class2.prototype, "fixTables"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "enableTableSupport", [_dec18], Object.getOwnPropertyDescriptor(_class2.prototype, "enableTableSupport"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "setTableCellBackground", [_dec19], Object.getOwnPropertyDescriptor(_class2.prototype, "setTableCellBackground"), _class2.prototype)), _class2)) || _class);
/**
 * The extension for a table row node.
 */

var TableRowExtension = (_dec20 = extension({
  defaultPriority: ExtensionPriority.Low
}), _dec20(_class4 = class TableRowExtension extends NodeExtension {
  get name() {
    return 'tableRow';
  }
  /**
   * Automatically create the `TableCellExtension` and
   * `TableHeaderCellExtension`. This is placed here so that this extension can
   * be tested independently from the `TableExtension`.
   */


  createExtensions() {
    return [new TableCellExtension({
      priority: ExtensionPriority.Low
    }), new TableHeaderCellExtension({
      priority: ExtensionPriority.Low
    })];
  }

  createNodeSpec(extra, override) {
    return createTableNodeSchema(extra, override).tableRow;
  }

}) || _class4);
/**
 * The extension for a table cell node.
 */

var TableCellExtension = (_dec21 = extension({
  defaultPriority: ExtensionPriority.Low
}), _dec21(_class5 = class TableCellExtension extends NodeExtension {
  get name() {
    return 'tableCell';
  }

  createNodeSpec(extra, override) {
    return createTableNodeSchema(extra, override).tableCell;
  }

}) || _class5);
/**
 * The extension for the table header node.
 */

var TableHeaderCellExtension = (_dec22 = extension({
  defaultPriority: ExtensionPriority.Low
}), _dec22(_class6 = class TableHeaderCellExtension extends NodeExtension {
  get name() {
    return 'tableHeaderCell';
  }

  createNodeSpec(extra, override) {
    return createTableNodeSchema(extra, override).tableHeaderCell;
  }

}) || _class6);
/**
 * The command for fixing the tables.
 */

function fixTablesCommand(lastGoodState) {
  return _ref => {
    var state = _ref.state,
        dispatch = _ref.dispatch;
    var tr = fixTables(state, lastGoodState);

    if (!tr) {
      return false;
    }

    if (dispatch) {
      dispatch(tr);
    }

    return true;
  };
}

function toggleMergeCellCommand(_ref2) {
  var state = _ref2.state,
      dispatch = _ref2.dispatch;

  if (mergeCells(state, dispatch)) {
    return false;
  }

  return splitCell(state, dispatch);
}

export { TableCellExtension, TableExtension, TableHeaderCellExtension, TableRowExtension, createTable, createTableOptions };
